const { debounce } = window.BAO.Utils

// Scrolling quickly can bring several blocks into view in rapid succession,
// so announcements are batched together rather than firing (and interrupting
// each other) once per block.
const ANNOUNCE_DEBOUNCE_MS = 750

class PlpEditorial extends window.BAO.CustomElement() {
  connectedCallback () {
    if (!this.els.block) return

    this.useStorage = this.dataset.templateStorageEnabled === 'true'

    // An array of the empty blocks elements, ready to be populated
    this.blockArray = []
    this.loadedBlockIndices = []
    this.preloadBlocksCount = parseInt(this.dataset.preloadBlocksCount, 10)

    this.pendingLoadedProductCount = 0
    this.announceLoadedBlocks = debounce(
      this.announceLoadedBlocks.bind(this),
      ANNOUNCE_DEBOUNCE_MS,
    )

    // Using the ?block query string, retrieve the indices of the blocks to be restored
    this.blocksToRestore = this.getBlocksToRestore()

    this.init()
  }

  async init () {
    // Fetch content for all blocks
    await this.getBlocks()

    // Now we have the block content, we can restore the relevant blocks
    this.restoreBlocks()

    // ...and watch for when subsequent empty blocks come into view
    this.setupIntersectionObserver()

    // Unloaded blocks are keyboard-focusable (see the `tabindex="0"` set in
    // the liquid template) so that tabbing quickly can't outrun scroll-based
    // loading and skip past them. Loading them is otherwise driven entirely
    // by scroll position via the IntersectionObserver above, which lags
    // behind focus when tabbing.
    this.addEventListener('focusin', this.handleBlockFocus.bind(this))
  }

  /**
   * Loads an unloaded block's content the moment it receives keyboard focus,
   * then moves focus into its first focusable element, so Tab can never
   * land on (or skip past) an empty placeholder.
   *
   * @param {FocusEvent} event
   * @returns {void}
   */
  handleBlockFocus (event) {
    const block = event.target.closest(this.els.block?.selector)
    if (!block || block.dataset.blockedLoaded !== 'false') return

    this.showBlock(block, true)

    const focusableSelector =
      'a[href]:not([tabindex="-1"]), button:not([disabled])'
    block.querySelector(focusableSelector)?.focus()
  }

  setupIntersectionObserver () {
    const options = {
      threshold: 0,
    }

    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.showBlock(entry.target, true)
        }
      })
    }, options)

    this.els?.block?.elements.forEach(block => {
      observer.observe(block)
    })
  }

  async getBlocks () {
    const storage = window.localStorage.getItem(this.storageName)

    if (!storage || storage === null || this.useStorage === false) {
      return window.BAO.sectionRenderer
        .fetch(this.dataset.id, `${window.location.href}`)
        .then(res => {
          const newDOM = window.BAO.sectionRenderer.convertToDOM(
            res[this.dataset.id],
          )

          const blocksContainer = newDOM.querySelector(this.els.blocks.selector)

          try {
            window.localStorage.setItem(
              this.storageName,
              blocksContainer.outerHTML,
            )
          } catch (error) {
            console.warn(error)
          }

          this.blockArray = newDOM.querySelectorAll(this.els.block.selector)
        })
        .catch(() => {})
    } else {
      const parser = new DOMParser()
      const doc = parser.parseFromString(storage, 'text/html')

      const container = doc.body.firstElementChild
      this.blockArray = container.querySelectorAll(this.els.block.selector)
    }
  }

  showBlock (block, updateUrl = false, dispatchEvent = false) {
    const blockIndex = parseInt(block.dataset.blockIndex0, 10)

    // Only load the block if it hasn't been loaded yet
    if (
      this.loadedBlockIndices.includes(blockIndex) ||
      blockIndex < this.preloadBlocksCount
    ) {
      return
    }

    if (updateUrl) this.updateUrl(blockIndex + 1)

    if (this.blockArray[blockIndex]) {
      block.innerHTML = this.blockArray[blockIndex]?.innerHTML

      block.setAttribute('data-blocked-loaded', 'true')
      block.removeAttribute('tabindex')

      this.loadedBlockIndices.push(blockIndex)

      this.pendingLoadedProductCount += block.querySelectorAll(
        '[data-product-card-el="card"]',
      ).length
      this.announceLoadedBlocks()

      if (dispatchEvent) {
        document.dispatchEvent(new CustomEvent('collectionRendered'))
      }
    }

    try {
      if (typeof iWish !== 'undefined') {
        setTimeout(() => {
          /* eslint-disable-next-line no-undef */
          iWish.checkIwishColl()
        }, 1500)
      }
    } catch (error) {
      console.warn(error)
    }
  }

  /**
   * Tells screen reader users that more content has loaded as they scroll,
   * since new blocks otherwise load silently. Called via a debounced
   * reference set up in connectedCallback, so a burst of blocks loading
   * from fast scrolling is announced once rather than once per block.
   *
   * @returns {void}
   */
  announceLoadedBlocks () {
    if (!this.els.announcer?.exists) return

    const count = this.pendingLoadedProductCount
    this.pendingLoadedProductCount = 0

    if (count === 0) {
      this.els.announcer.element.textContent =
        window.theme.strings.contentLoaded
      return
    }

    const template =
      count === 1
        ? window.theme.strings.productsLoadedOne
        : window.theme.strings.productsLoadedOther

    this.els.announcer.element.textContent = template.replace(
      '[[COUNT]]',
      count,
    )
  }

  restoreBlocks () {
    if (
      !Array.isArray(this.blocksToRestore) ||
      this.blocksToRestore.length <= 0 ||
      !this.els?.block?.elements
    ) {
      return
    }
    this.blocksToRestore.forEach((blockIndex, index) => {
      const isLast = index === this.blocksToRestore.length - 1
      const blockElement = this.els?.block?.elements.find(
        el => el.getAttribute('data-block-index') === String(blockIndex),
      )
      if (blockElement) this.showBlock(blockElement, false, isLast)
    })
  }

  updateUrl (blockIndex = false) {
    const url = new URL(window.location)

    if (blockIndex) {
      url.searchParams.set('block', blockIndex)
    } else {
      url.searchParams.delete('block')
    }

    window.history.pushState({}, '', url)
  }

  getBlocksToRestore () {
    const params = new URLSearchParams(window.location.search)
    const blockValue = parseInt(params.get('block'), 10)

    // Return an empty array if blockValue is not a number or is within the preloadBlocksCount
    if (isNaN(blockValue) || blockValue <= this.preloadBlocksCount) {
      return []
    }

    // Return an array of block indexes that need to be updated
    const blocksToUpdate = []
    for (let i = this.preloadBlocksCount + 1; i <= blockValue; i++) {
      blocksToUpdate.push(i)
    }

    return blocksToUpdate
  }

  get storageName () {
    return `BAO-plp-editorial-${this.dataset.templateSuffix}-${this.dataset.templateVersion}`
  }
}

if (!customElements.get('plp-editorial')) {
  customElements.define('plp-editorial', PlpEditorial)
}
